import axios from "axios";
import CommonService from "../common/CommonService";
import StorageService from "../storage/StorageService";

const URL = {
  base: "/api/cdk/payment/v1",
  paymentInfo: "/payment-info/",
  serviceView: "/service-view/",
  validateSubscription: "/validate-payment-subscription/",
  invoiceSearch: "/validate-invoice-exist?invoiceId=",
  partsService: "/parts-service-pay/",
  inviteToPay: "/invite-to-pay/",
  initiateAccounting: "/accounting-initiate/",
  accounting: "/accounting-pay/",
  accountingInviteToPay: "/accounting-invite-to-pay/",
  cancel: "/cancel-payment/",
  refund: "/refund-payment/",
  bypass: "/bypass-payment/",
  paymentStatus: "/payment-status/",
  paymentPostback: "/payment-postback/"
};

const HEADERS = {
  "Content-Type": "application/json"
};

const STORAGE_KEYS = {
  MERCHANT: "MERCHANT",
  TERMINAL: "TERMINAL",
  CASHIER: "CASHIER",
  DEALER_NAME: "DEALER_NAME"
};

const getErrorMessage = (defaultErrorMessage, error) => {
  console.error(defaultErrorMessage, error);

  if (
    error.response &&
    error.response.status &&
    error.response.status === 400 &&
    error.response.data &&
    error.response.data.message
  ) {
    return { isError: true, message: error.response.data.message };
  }

  return { isError: true, message: defaultErrorMessage };
};

const PaymentService = {
  getPaymentData: async () => {
    // X click to source
    CommonService.registerOnUnloadListener();

    const response = await axios.get(`${URL.base}${URL.paymentInfo}`, {
      headers: HEADERS
    });
    return response.data;
  },
  getServiceViewMapping: async () => {
    const response = await axios.get(`${URL.base}${URL.serviceView}`, {
      headers: HEADERS
    });
    return response.data;
  },
  validateSubscription: async () => {
    const response = await axios.get(`${URL.base}${URL.validateSubscription}`, {
      headers: HEADERS
    });
    return response.data;
  },
  validateInvoiceTransaction: async (documentId) => {
    const response = await axios.get(`${URL.base}${URL.invoiceSearch}${documentId}`, {
      headers: HEADERS
    });
    return response.data;
  },
  sendPartsServicePayment: async paymentData => {
    StorageService.addItem(STORAGE_KEYS.MERCHANT, paymentData.mid);
    StorageService.addItem(STORAGE_KEYS.TERMINAL, paymentData.terminal);

    try {
      const response = await axios.post(
        `${URL.base}${URL.partsService}`,
        paymentData,
        { headers: HEADERS }
      );

      // Remove X click to source
      CommonService.unregisterOnUnloadListener();

      return response.data;
    } catch (e) {
      return getErrorMessage(
        "An error has occurred while trying to send the payment!",
        e
      );
    }
  },
  sendInviteToPayPayment: async paymentData => {
    StorageService.addItem(STORAGE_KEYS.MERCHANT, paymentData.mid);

    try {
      const response = await axios.post(
        `${URL.base}${URL.inviteToPay}`,
        paymentData,
        { headers: HEADERS }
      );
      const paymentInfo = response.data;

      return {
        isSuccess: true,
        info: paymentInfo,
        message: `Your Invite to Pay payment was sent to an email ${paymentData.email}. The window will close automatically in 5 seconds.`
      };
    } catch (e) {
      return getErrorMessage(
        "An error has occurred while trying to send the payment!",
        e
      );
    }
  },
  sendAccountingPayment: async paymentData => {
    StorageService.addItem(STORAGE_KEYS.MERCHANT, paymentData.mid);
    StorageService.addItem(STORAGE_KEYS.TERMINAL, paymentData.terminal);
    StorageService.addItem(STORAGE_KEYS.DEALER_NAME, paymentData.dealerStoresName);

    try {
      const response = await axios.post(
        `${URL.base}${URL.accounting}`,
        paymentData,
        { headers: HEADERS }
      );

      // Remove X click to source
      CommonService.unregisterOnUnloadListener();

      return response.data;
    } catch (e) {
      return getErrorMessage(
        "An error has occurred while trying to send the payment!",
        e
      );
    }
  },
  sendAccountingInviteToPayPayment: async paymentData => {
    StorageService.addItem(STORAGE_KEYS.MERCHANT, paymentData.mid);
    StorageService.addItem(STORAGE_KEYS.CASHIER, paymentData.cashier);

    try {
      const response = await axios.post(
        `${URL.base}${URL.accountingInviteToPay}`,
        paymentData,
        { headers: HEADERS }
      );

      const paymentInfo = response.data;

      return {
        isSuccess: true,
        info: paymentInfo,
        message: `Your Invite to Pay payment was sent to an email ${paymentData.email}. The window will close automatically in 5 seconds.`
      };

    } catch (e) {
      return getErrorMessage(
        "An error has occurred while trying to send the payment!",
        e
      );
    }
  },
  sendCancelPayment: async refundData => {
    StorageService.addItem(STORAGE_KEYS.MERCHANT, refundData.mid);

    try {
      const response = await axios.post(
        `${URL.base}${URL.cancel}`,
        refundData,
        { headers: HEADERS }
      );

      // Remove X click to source
      CommonService.unregisterOnUnloadListener();

      return response.data;
    } catch (e) {
      return getErrorMessage(
        "An error has occurred while trying to cancel the payment!",
        e
      );
    }
  },
  sendRefund: async refundData => {
    StorageService.addItem(STORAGE_KEYS.MERCHANT, refundData.mid);

    try {
      const response = await axios.post(
        `${URL.base}${URL.refund}`,
        refundData,
        { headers: HEADERS }
      );

      // Remove X click to source
      CommonService.unregisterOnUnloadListener();

      return response.data;
    } catch (e) {
      return getErrorMessage(
        "An error has occurred while trying to refund the payment!",
        e
      );
    }
  },
  sendBypassPayment: async () => {
    try {
      const response = await axios.post(
        `${URL.base}${URL.bypass}`,
        {},
        { headers: HEADERS }
      );

      // Remove X click to source
      CommonService.unregisterOnUnloadListener();

      return response.data;
    } catch (e) {
      console.error(
        "An error has occurred while trying to bypass the payment",
        e
      );
      return {
        isError: true,
        message: "An error has occurred while trying to bypass the payment!"
      };
    }
  },
  validatePaymentStatus: async () => {
    try {
      // X click to source
      CommonService.registerOnUnloadListener();

      const response = await axios.get(`${URL.base}${URL.paymentStatus}`, {
        headers: HEADERS
      });
      const paymentInfo = response.data;

      if (paymentInfo.status === "CREATED")
        return { isError: true, message: "The payment has an invalid status!" };

      if (
        !paymentInfo.paymentAttempts ||
        paymentInfo.paymentAttempts.length === 0
      )
        return {
          isError: true,
          message: "The payment information is missing for this payment!"
        };

      if (!paymentInfo.paymentAttempts.some(pa => pa.status === "SUCCESS"))
        return {
          isError: true,
          message: "No successful payments were found for this payment!"
        };

      return { isSuccess: true };
    } catch (e) {
      console.error(
        "An error has occurred while trying to get the payment information!",
        e
      );
      return {
        isError: true,
        message:
          "An error has occurred while trying to get the payment information!"
      };
    }
  },
  postback: async () => {
    try {
      const response = await axios.post(
        `${URL.base}${URL.paymentPostback}`,
        {},
        { headers: HEADERS }
      );
      const paymentInfo = response.data;

      // Remove X click to source
      CommonService.unregisterOnUnloadListener();

      return { isSuccess: true, postbackInfo: paymentInfo };
    } catch (e) {
      console.error(
        "An error has occurred while trying to get the payment information!",
        e
      );

      if (e.response && e.response.status && e.response.status === 404)
        return {
          isError: true,
          message:
            "Please verify if the payment type(s) used for this payment are configured in the Customer!"
        };
      else if (e.response && e.response.status && e.response.status === 400)
        return {
          isWarning: true,
          message:
            "Return was applied, but posting errors resulted.  Correct document through AGJE."
        };

      return {
        isError: true,
        message:
          "An error has occurred while trying to send the payment information to the Customer!"
      };
    }
  }
};

export default PaymentService;
